import { Box } from '@shared/components';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

type CameraMode = 'live' | 'captured';

export interface CameraProps {
  width: number;
  height: number;
  borderType?: 'round' | 'rectangle';
  facingMode?: 'user' | 'environment';
  imageFormat?: 'image/jpeg' | 'image/png';
  imageQuality?: number;
  visible?: boolean;
  onError?: (error: Error) => void;
  onCapture?: (blob: Blob) => void;
  onStart?: () => void;
}

export interface CameraRef {
  capture: () => Promise<void>;
  resetCamera: () => void;
  stopCamera: () => void;
  startCamera: () => Promise<void>;
}

export const Camera = forwardRef<CameraRef, CameraProps>(
  (
    {
      width,
      height,
      borderType = 'rectangle',
      onError,
      onCapture,
      onStart,
      facingMode = 'user',
      imageFormat = 'image/jpeg',
      imageQuality = 1.0,
      visible: shouldVisible = true,
    }: CameraProps,
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const isMountedRef = useRef<boolean>(true);

    const [mode, setMode] = useState<CameraMode>('live');

    const stopCamera = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };

    const startCamera = async () => {
      if (!isMountedRef.current) {
        return;
      }

      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: width },
            height: { ideal: height },
            facingMode,
          },
          audio: false,
        });

        // Double-check if component is still mounted after async operation
        if (!isMountedRef.current) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }

        streamRef.current = mediaStream;
        onStart?.();
      } catch (error) {
        if (isMountedRef.current) {
          const cameraError = new Error(
            `Camera access failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          );
          onError?.(cameraError);
        }
      }
    };

    const capture = async () => {
      if (!videoRef.current || !canvasRef.current || !streamRef.current) {
        onError?.(new Error('Camera not ready for capture'));
        return;
      }

      try {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        const context = canvas.getContext('2d');

        if (!context) {
          throw new Error('Could not get canvas context');
        }

        canvas.width = width;
        canvas.height = height;
        context.drawImage(video, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob && onCapture) {
              onCapture(blob);
            }
          },
          imageFormat,
          imageQuality,
        );

        setMode('captured');
      } catch (error) {
        const captureError = new Error(
          `Image capture failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
        onError?.(captureError);
      }
    };

    useImperativeHandle(ref, () => ({
      capture,
      resetCamera: () => {
        setMode('live');
      },
      stopCamera,
      startCamera,
    }));

    useEffect(() => {
      isMountedRef.current = true;

      if (!shouldVisible) {
        stopCamera();
      }

      return () => {
        isMountedRef.current = false;
        stopCamera();
      };
    }, [width, height, facingMode, onError, shouldVisible]);

    // Empty block to prevent from rendering anything when not visible
    if (!shouldVisible) {
      return <canvas ref={canvasRef} style={{ display: 'none' }} />;
    }

    return (
      <>
        {/* Live video feed */}
        <Box
          component='video'
          ref={videoRef}
          width={width}
          height={height}
          autoPlay
          playsInline
          muted
          sx={{
            objectFit: 'cover',
            display: mode === 'live' ? 'block' : 'none',
            borderRadius: (theme) => theme.spacing(borderType === 'round' ? 2 : 0),
          }}
        />
        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </>
    );
  },
);

Camera.displayName = 'Camera';

export default Camera;
